import { Check, UserPlus, X } from 'lucide-react';
import { Avatar, Button } from '@/components/ui';
import { useAuth } from '@/app/providers';
import { useFriendRequestActions, useIncomingFriendRequests } from '../hooks/useSocial';
import { stripHandle } from '../helpers';

const SECTION_LABEL =
  'mb-3 flex items-center gap-2 text-[11px] font-extrabold uppercase tracking-[0.7px] text-lo';

export function FriendRequestsPanel() {
  const { user } = useAuth();
  const { data: requests = [], isLoading } = useIncomingFriendRequests(user?.id);
  const { accept, deny } = useFriendRequestActions(user?.id);

  return (
    <section>
      <div className={SECTION_LABEL}>
        <UserPlus size={13} strokeWidth={2.4} />
        Friend requests
        {requests.length > 0 && <span className="text-accent">{requests.length}</span>}
      </div>

      {isLoading ? (
        <p className="text-sm text-lo">Checking requests…</p>
      ) : requests.length === 0 ? (
        <p className="text-sm leading-relaxed text-lo">
          Incoming friend requests will land here so you can accept or deny them.
        </p>
      ) : (
        <ul className="divide-y divide-white/[0.06] border-y border-white/[0.06]">
          {requests.map((request) => {
            const profile = request.requester;
            const name = profile?.display_name || stripHandle(profile?.handle) || 'WINR member';
            return (
              <li key={request.id} className="flex items-center gap-3 py-3">
                <Avatar src={profile?.avatar_url} name={name} size={36} className="flex-none" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-bold text-hi">{name}</p>
                  {profile?.handle && (
                    <p className="truncate text-[12px] text-lo">@{stripHandle(profile.handle)}</p>
                  )}
                </div>
                <Button
                  size="sm"
                  className="h-8 w-8 rounded-full px-0"
                  title="Accept"
                  disabled={accept.isPending || deny.isPending}
                  onClick={() => accept.mutate(request)}
                >
                  <Check size={14} />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-8 w-8 rounded-full px-0 text-lo hover:text-hi"
                  title="Deny"
                  disabled={accept.isPending || deny.isPending}
                  onClick={() => deny.mutate(request.id)}
                >
                  <X size={14} />
                </Button>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
